import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { spesifics } from "@/config/endpoints";
import { getCfDisplayName } from "@/features/customField/utils/cfDisplayName";

export default function useSpesificCustoms({
  queryKey,
  modelName,
  type,
  stageId,
  campaignId,
  /** Filter-sheet mode: include campaign-scoped fields (Meta question CFs). */
  allCampaigns = false,
  isActive = true,
}: any) {
  const query = useQuery({
    queryKey: [
      queryKey,
      modelName,
      stageId ?? null,
      campaignId ?? null,
      allCampaigns,
    ],
    queryFn: async () => {
      let url = `${spesifics}?modelName=${encodeURIComponent(modelName)}`;
      if (stageId) url += `&stageId=${stageId}`;
      if (campaignId) url += `&campaignId=${campaignId}`;
      if (allCampaigns) url += `&allCampaigns=1`;
      const res = await axiosInstance.get(url);
      return res.data.inputs;
    },
    enabled: isActive,
  });

  // Memoize the derived arrays so the references are stable across
  // renders when the underlying query data hasn't changed. Without
  // these, `.filter()` / `.map()` created fresh arrays every render
  // and any consumer with `useEffect`/`useLayoutEffect` deps on them
  // (e.g. PrefrenceFormRenderer's auto-align effect) re-ran every
  // render — which combined with parent `watch()` subscriptions
  // produced infinite render loops (React error #185).
  const spesificFieldSelect = useMemo(
    () =>
      query.data?.map((val: any) => ({
        value: String(val.id),
        // Display artifact — user label wins; raw rows stay in spesificFields.
        name: getCfDisplayName(val),
      })) ?? [],
    [query.data],
  );

  const spesificFields = useMemo(() => {
    if (!query.data) return query.data;
    if (type) return query.data.filter((val: any) => val.type === type);
    return query.data;
  }, [query.data, type]);

  return {
    spesificFields,
    spesificFieldSelect,
    length: query.data ? query.data.length : 0,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
